<template>
  <div class="uplift-container v-card">
    <mf-loading :boolean-loader="loadingReferenceData" loader-type="dynamic" :dynamic-amount="4">
      <div v-if="!isPdfContent" class="uplift-header">
        <div class="header-text">
          <h2 class="header-text-title">Uplift</h2>
          <div class="header-text-description">
            Comparação da performance dos produtos no
            <span class="plain-text-highlighted-font">período da oferta x mesmo período do mês anterior</span>

            . Os dados comparados levam em consideração consumidores do clube e fora clube,
            <span class="plain-text-highlighted-font">independente da aplicação da oferta</span>, por isso algumas métricas podem ter valores diferentes do
            restante da tela.
          </div>
        </div>
        <div>
          <export-data button-label="Exportar dados por produto" :data-fetcher="getDataToExport" filename="uplift_por_produto" ghost></export-data>
        </div>
      </div>
      <div class="uplift-data">
        <div class="data-headers">
          <div></div>
          <div>
            <mf-icon medium color="marca-0-l20">payments</mf-icon>
            <span class="header-text">Faturamento</span>
          </div>
          <div>
            <mf-icon medium color="marca-0-l20">sell</mf-icon>
            <span class="header-text">Unidades vendidas</span>
          </div>
          <div>
            <mf-icon medium color="marca-0-l20">shopping_cart</mf-icon>
            <span class="header-text">Número de cupons</span>
          </div>
          <div>
            <mf-icon medium color="marca-0-l20">group</mf-icon>
            <span class="header-text">Consumidores</span>
          </div>
        </div>
        <div class="data-offered-period">
          <mf-tooltip max-width="250" top>
            <template #activator="{ on }">
              <div>Período ofertado</div>
            </template>
            {{ `${offeredMetrics.startDate} - ${offeredMetrics.endDate}` }}
          </mf-tooltip>
          <div>R$ {{ offeredMetrics.revenue | localeString(2) }}</div>
          <div>{{ offeredMetrics.quantity | localeString(0) }}</div>
          <div>{{ offeredMetrics.tickets | localeString(0) }}</div>
          <div>{{ offeredMetrics.clients | localeString(0) }}</div>
        </div>
        <div class="data-previous-period">
          <div style="max-width: 225px">
            <div style="max-width: 195px">
              <div>Período anterior</div>
              <div v-if="!isPdfContent" class="d-flex flew-row align-center">
                <mf-range-picker
                  :max="maxReferenceDate"
                  :initial-date.sync="referenceStartDate"
                  :final-date.sync="referenceFinalDate"
                  @change="referenceDateUpdated"
                />
                <mf-tooltip v-if="numReferenceDays && numReferenceDays !== numCurrentDays" max-width="250" top>
                  <template #activator="{ on }">
                    <mf-icon fill size="16" class="pl-1">warning</mf-icon>
                  </template>
                  <span>
                    O período anterior selecionado não tem a mesma quantidade de dias do que o período ofertado. O período anterior tem {{ numReferenceDays }}
                    {{ numReferenceDays === 1 ? 'dia' : 'dias' }} enquanto o período ofertado tem {{ numCurrentDays }}
                    {{ numCurrentDays === 1 ? 'dia' : 'dias' }}.
                  </span>
                </mf-tooltip>
              </div>
            </div>
          </div>
          <div>R$ {{ previousMetrics.revenue | localeString(2) }}</div>
          <div>{{ previousMetrics.quantity | localeString(0) }}</div>
          <div>{{ previousMetrics.tickets | localeString(0) }}</div>
          <div>{{ previousMetrics.clients | localeString(0) }}</div>
        </div>
        <div class="data-percentages">
          <div></div>
          <div><mf-stonks variant="percentage" :percentage="variations.revenue" /></div>
          <div><mf-stonks variant="percentage" :percentage="variations.quantity" /></div>
          <div><mf-stonks variant="percentage" :percentage="variations.tickets" /></div>
          <div><mf-stonks variant="percentage" :percentage="variations.clients" /></div>
        </div>
      </div>
      <div v-if="!isPdfContent" class="uplift-expansion">
        <mf-button
          text
          :label="`${expandUpflit ? 'Minimizar' : 'Ver'} uplift por produto`"
          :icon="`expand_${expandUpflit ? 'less' : 'more'}`"
          text-transform="none"
          color="marca-0-l40"
          @click="expandUpflit = !expandUpflit"
        />
      </div>
      <uplift-chart v-if="expandUpflit" :metrics="productsMetricsTable" @export-data="downloadCsv" />
    </mf-loading>
  </div>
</template>

<script>
import MfStonks from '@/modules/maria/produtos-impulsionadores/views/components/Step1/MfStonks.vue'
import MfRangePicker from '@/components/MFRangePicker.vue'
import ExportData from '@/components/export-data/ExportData.vue'
import UpliftChart from './UpliftChart.vue'
import { localeString, createCsvFromArray } from '@/config/helpers.js'
import { QUERY_GET_KPI_METRICS, QUERY_GET_KPI_METRICS_BY_PRODUCTS } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'

export default {
  name: 'UpliftData',
  components: {
    MfRangePicker,
    MfStonks,
    ExportData,
    UpliftChart,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    isPdfContent: {
      type: Boolean,
      default: false,
    },
    metrics: {
      type: [Array, Object],
      default: () => [],
    },
    periods: {
      type: Object,
      default: () => {},
    },
    products: {
      type: Array,
      default: () => [],
    },
    units: {
      type: Array,
      default: () => [],
    },
    offerType: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    menu: false,
    date: [],
    expandUpflit: false,
    loadingReferenceData: false,
    usingCustomReferenceMetrics: false,
    referenceStartDate: '',
    referenceFinalDate: '',
    customReferenceMetrics: {},
    customReferenceMetricsByProducts: {},
  }),
  computed: {
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),

    defaultPickerDate() {
      return this.referenceStartDate || this.periods.offered.startDate
    },
    numReferenceDays() {
      if (!this.referenceStartDate || !this.referenceFinalDate) return 0
      return DateTime.fromFormat(this.referenceFinalDate, 'yyyy-MM-dd').diff(DateTime.fromFormat(this.referenceStartDate, 'yyyy-MM-dd'), 'days').days + 1
    },
    numCurrentDays() {
      if (!this.offeredMetrics.startDate || !this.offeredMetrics.endDate) return 0
      return (
        DateTime.fromFormat(this.offeredMetrics.endDate, 'dd/MM/yyyy').diff(DateTime.fromFormat(this.offeredMetrics.startDate, 'dd/MM/yyyy'), 'days').days + 1
      )
    },
    maxReferenceDate() {
      if (!this.lastUpdate?.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
    productsMetricsTable() {
      if (this.usingCustomReferenceMetrics) {
        return this.metrics?.products_metrics?.table.map((productMetric) => ({
          ...productMetric,
          reference: this.customReferenceMetricsByProducts[productMetric.product.sku] ?? {
            revenue: 0,
            quantity: 0,
            tickets: 0,
            clients: 0,
          },
        }))
      }
      return this.metrics?.products_metrics?.table
    },
    previousMetrics() {
      if (this.usingCustomReferenceMetrics) {
        return this.customReferenceMetrics
      }
      return {
        revenue: this.metrics?.products_metrics?.totals?.reference?.revenue || 0,
        quantity: this.metrics?.products_metrics?.totals?.reference?.quantity || 0,
        tickets: this.metrics?.products_metrics?.totals?.reference?.tickets || 0,
        clients: this.metrics?.products_metrics?.totals?.reference?.clients || 0,
      }
    },
    offeredMetrics() {
      return {
        revenue: this.metrics?.products_metrics?.totals?.total?.revenue || 0,
        quantity: this.metrics?.products_metrics?.totals?.total?.quantity || 0,
        tickets: this.metrics?.products_metrics?.totals?.total?.tickets || 0,
        clients: this.metrics?.products_metrics?.totals?.total?.clients || 0,
        startDate: DateTime.fromFormat(this.periods.offered.startDate.toString(), 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
        endDate: DateTime.fromFormat(this.periods.offered.endDate.toString(), 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
      }
    },
    variations() {
      return {
        revenue: this.previousMetrics.revenue ? (100 * (this.offeredMetrics.revenue - this.previousMetrics.revenue)) / this.previousMetrics.revenue : 100,
        quantity: this.previousMetrics.quantity ? (100 * (this.offeredMetrics.quantity - this.previousMetrics.quantity)) / this.previousMetrics.quantity : 100,
        tickets: this.previousMetrics.tickets ? (100 * (this.offeredMetrics.tickets - this.previousMetrics.tickets)) / this.previousMetrics.tickets : 100,
        clients: this.previousMetrics.clients ? (100 * (this.offeredMetrics.clients - this.previousMetrics.clients)) / this.previousMetrics.clients : 100,
      }
    },
    allSkus() {
      return this.products?.map((product) => product.sku)
    },
    unitIds() {
      return this.units?.map((unit) => unit.id_erp)
    },
    warningMissingPrecalculatedResults() {
      return `<span style="color: #B58943">Sem dados pré calculados para o período anterior. É possível avaliar um período anterior selecionando uma data ao lado.</span>`
    },
    allOfferDates() {
      if (!this.periods.offered.startDate || !this.periods.offered.endDate) return []

      const datesArray = []
      let currentDate = DateTime.fromISO(this.periods.offered.startDate).startOf('day')
      const end = DateTime.fromISO(this.periods.offered.endDate).startOf('day')

      while (currentDate <= end) {
        datesArray.push(currentDate.toFormat('yyyy-MM-dd'))
        currentDate = currentDate.plus({ days: 1 })
      }
      return datesArray
    },
  },
  watch: {
    offerType() {
      this.usingCustomReferenceMetrics = false
      this.referenceStartDate = this.periods?.previous?.startDate ?? ''
      this.referenceFinalDate = this.periods?.previous?.endDate ?? ''
    },
  },
  mounted() {
    this.referenceStartDate = this.periods?.previous?.startDate ?? ''
    this.referenceFinalDate = this.periods?.previous?.endDate ?? ''
  },
  methods: {
    localeString,
    async referenceDateUpdated() {
      if (!this.allSkus?.length) {
        this.$snackbar({ message: 'Não é possível calcular métricas do período anterior', snackbarColor: 'error' })
        return
      }
      this.loadingReferenceData = true
      try {
        await Promise.all([this.loadReferenceMetricsKpi(), this.loadReferenceMetricsKpiByProducts()])
        this.usingCustomReferenceMetrics = true
      } catch (error) {
        console.warn(error)
        this.$snackbar({ message: `Erro ao buscar métricas do período anterior`, snackbarColor: 'error' })
      }
      this.loadingReferenceData = false
    },
    async downloadCsv() {
      const csvData = this.getDataToExport()
      createCsvFromArray(csvData.rows, 'uplift_por_produto', csvData.headers)
    },
    async loadReferenceMetricsKpi() {
      const { data: kpiMetricsData } = await this.$apollo.query({
        query: QUERY_GET_KPI_METRICS,
        variables: {
          initialDate: this.referenceStartDate,
          finalDate: this.referenceFinalDate,
          products: this.allSkus,
          units: this.unitIds,
        },
        context: {
          uri: this.$microservicesUrls['campaigns'],
        },
      })
      this.customReferenceMetrics = {
        revenue: kpiMetricsData.metricsKpi.revenue[0].value,
        quantity: kpiMetricsData.metricsKpi.total_items[0].value,
        tickets: kpiMetricsData.metricsKpi.total_coupons[0].value,
        clients: kpiMetricsData.metricsKpi.unique_customers[0].value,
      }
    },
    async loadReferenceMetricsKpiByProducts() {
      const { data: kpiMetricsByProductsData } = await this.$apollo.query({
        query: QUERY_GET_KPI_METRICS_BY_PRODUCTS,
        variables: {
          initialDate: this.referenceStartDate,
          finalDate: this.referenceFinalDate,
          products: this.allSkus,
          units: this.unitIds,
        },
        context: {
          uri: this.$microservicesUrls['campaigns'],
        },
      })
      this.customReferenceMetricsByProducts = kpiMetricsByProductsData.metricsKpiByProducts.reduce((acc, metricByProduct) => {
        return {
          ...acc,
          [metricByProduct.product.sku]: {
            revenue: metricByProduct.metrics.revenue[0].value,
            quantity: metricByProduct.metrics.total_items[0].value,
            tickets: metricByProduct.metrics.total_coupons[0].value,
            clients: metricByProduct.metrics.unique_customers[0].value,
          },
        }
      }, {})
    },
    getDataToExport() {
      const exportData = {
        headers: [
          'SKU',
          'Nome do produto',
          'Faturamento período anterior',
          'Faturamento período ofertado',
          'Unidades vendidas período anterior',
          'Unidades vendidas período ofertado',
          'Número de cupons período anterior',
          'Número de cupons período ofertado',
          'Consumidores período anterior',
          'Consumidores período ofertado',
        ],
        rows: this.productsMetricsTable.map((metric) => [
          metric.product.sku,
          metric.product.name,
          metric.reference.revenue,
          metric.total.revenue,
          metric.reference.quantity,
          metric.total.quantity,
          metric.reference.tickets,
          metric.total.tickets,
          metric.reference.clients,
          metric.total.clients,
        ]),
      }
      return exportData
    },
  },
}
</script>

<style lang="scss" scoped>
.uplift-container {
  background: white;
  padding: 24px 16px !important;
  flex-direction: column;
  border-radius: 8px;
  display: flex;
  gap: 20px;
  margin-top: 12px;

  .uplift-header {
    margin-bottom: 20px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    .header-text {
      h2 {
        color: $cinza-d10;
        font-size: 20px;
        font-weight: 700;
        line-height: 24px;
        margin-bottom: 6px;
      }
      .header-text-description {
        max-width: 95%;
        color: $cinza-0;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        span {
          font-weight: 600;
        }
        .plain-text-highlighted-font {
          color: $marca-1;
        }
      }
    }
  }
  .uplift-data {
    display: flex;
    flex-direction: column;
    gap: 8px;
    > div {
      display: grid;
      grid-template-columns: 24% 19% 19% 19% 19%;
    }
    .data-headers {
      div {
        min-height: 32px;
        display: flex;
        gap: 6px;
        .header-text {
          color: $marca-0-l20;
          font-size: 14px;
          font-weight: 600;
          line-height: 17px;
          letter-spacing: 0.4px;
        }
      }
    }
    .data-offered-period {
      color: $cinza-d20;
      font-size: 18px;
      font-weight: 400;
      line-height: 22px;
    }
    .data-previous-period {
      color: $cinza-0;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      align-items: center;
    }
    .data-percentages {
      margin-top: 5px;
    }
  }
  .uplift-expansion {
    ::v-deep .v-btn__content {
      font-size: 14px !important;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: 0.01em;

      .mf-icon {
        font-size: 18px !important;
      }
    }
  }
}
::v-deep .icon-container {
  margin-left: 0px !important;
}
</style>
